import { CheckboxGroup, CheckboxGroupItem } from '@undp/design-system-react/CheckboxGroup';
import { createFilter, DropdownSelect } from '@undp/design-system-react/DropdownSelect';
import { Label } from '@undp/design-system-react/Label';
import { RadioGroup, RadioGroupItem } from '@undp/design-system-react/RadioGroup';
import { Spinner } from '@undp/design-system-react/Spinner';
import flattenDeep from 'lodash.flattendeep';
import intersection from 'lodash.intersection';
import { useCallback, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react';
import { Colors } from '@/Components/ColorPalette';
import { ColorLegend } from '@/Components/Elements/ColorLegend';
import { GraphFooter } from '@/Components/Elements/GraphFooter';
import { GraphHeader } from '@/Components/Elements/GraphHeader';
import type {
  AdvancedDataSelectionDataType,
  AggregationSettingsDataType,
  DataFilterDataType,
  DataSelectionDataType,
  DataSettingsDataType,
  FilterSettingsDataType,
  FilterUiSettingsDataType,
  GraphConfigurationDataType,
  GraphSettingsDataType,
  GraphType,
} from '@/Types';
import { checkIfMultiple } from '@/Utils/checkIfMultiple';
import { checkIfNullOrUndefined } from '@/Utils/checkIfNullOrUndefined';
import {
  fetchAndParseCSV,
  fetchAndParseJSON,
  fetchAndParseMultipleDataSources,
  fetchAndTransformDataFromAPI,
} from '@/Utils/fetchAndParseData';
import { getUniqValue } from '@/Utils/getUniqValue';
import { filterData } from '@/Utils/transformData/filterData';
import { transformColumnsToArray } from '@/Utils/transformData/transformColumnsToArray';
import { transformDataForAggregation } from '@/Utils/transformData/transformDataForAggregation';
import { transformDataForGraph } from '@/Utils/transformData/transformDataForGraph';
import { transformDefaultValue } from '@/Utils/transformDataForSelect';
import { GraphContainer } from '../Elements/GraphContainer';
import GraphEl from './GraphEl';

interface Props {
  noOfColumns?: number;
  columnGridBy: string;
  graphSettings?: GraphSettingsDataType;
  dataSettings: DataSettingsDataType;
  filters?: FilterUiSettingsDataType[];
  noOfFiltersPerRow?: number;
  graphType: Exclude<GraphType, 'geoHubMap' | 'geoHubCompareMap' | 'geoHubMapWithLayerSelection'>;
  dataTransform?: {
    keyColumn: string;
    aggregationColumnsSetting?: AggregationSettingsDataType[];
  };
  showCommonColorScale?: boolean;
  dataFilters?: DataFilterDataType[];
  graphDataConfiguration?: GraphConfigurationDataType[];
  dataSelectionOptions?: DataSelectionDataType[];
  advancedDataSelectionOptions?: AdvancedDataSelectionDataType[];
  minGraphHeight?: number;
  minGraphWidth?: number;
  debugMode?: boolean;
  readableHeader?: {
    value: string;
    label: string;
  }[];
  uiMode?: 'light' | 'normal';
  theme?: 'dark' | 'light';
}

export function GriddedGraphs(props: Props) {
  const {
    graphSettings,
    dataSettings,
    filters,
    graphType,
    dataTransform,
    graphDataConfiguration,
    noOfColumns,
    columnGridBy,
    dataFilters,
    showCommonColorScale,
    minGraphHeight,
    minGraphWidth,
    debugMode,
    dataSelectionOptions,
    advancedDataSelectionOptions,
    readableHeader,
    noOfFiltersPerRow = 4,
    uiMode = 'normal',
    theme = 'light',
  } = props;
  // biome-ignore lint/suspicious/noExplicitAny: undefined data type
  const [data, setData] = useState<any>(undefined);
  // biome-ignore lint/suspicious/noExplicitAny: undefined data type
  const [dataFromFile, setDataFromFile] = useState<any>(undefined);
  const [gridOption, setGridOption] = useState<(string | number)[]>([]);
  const graphParentDiv = useRef<HTMLDivElement>(null);
  const [graphConfig, setGraphConfig] = useState<GraphConfigurationDataType[] | undefined>(
    graphDataConfiguration,
  );
  const [filterSettings, setFilterSettings] = useState<FilterSettingsDataType[]>([]);
  const [advancedGraphSettings, setAdvancedGraphSettings] = useState<GraphSettingsDataType>({});

  const filterConfig = useMemo(
    () => ({
      ignoreCase: true,
      ignoreAccents: true,
      trim: true,
    }),
    [],
  );

  const filteredData = useMemo(() => {
    if (!dataFromFile || filterSettings.length === 0) return dataFromFile;
    // biome-ignore lint/suspicious/noExplicitAny: undefined data type
    const result = dataFromFile.filter((item: any) =>
      filterSettings.every((filter) =>
        filter.value && flattenDeep([filter.value]).length > 0
          ? intersection(
              flattenDeep([item[filter.filter]]),
              flattenDeep([filter.value]).map((el) => el.value),
            ).length > 0
          : true,
      ),
    );
    return result;
  }, [filterSettings, dataFromFile]);
  useEffect(() => {
    setData(filteredData);
  }, [filteredData]);

  const updateFiltersEvent = useEffectEvent(() => {
    const filterSettingsTemp = (filters || []).map((el) => ({
      filter: el.column,
      label: el.label || `Filter by ${el.column}`,
      singleSelect: el.singleSelect,
      clearable: el.clearable,
      ui: el.ui,
      defaultValue: transformDefaultValue(el.defaultValue),
      value: transformDefaultValue(el.defaultValue),
      availableValues: getUniqValue(dataFromFile, el.column)
        .filter((v) => !el.excludeValues?.includes(`${v}`))
        .map((v) => ({ value: v, label: v })),
      allowSelectAll: el.allowSelectAll,
      width: el.width,
    }));
    setFilterSettings(filterSettingsTemp);
  });
  useEffect(() => {
    const fetchData = async () => {
      try {
        const fetchDataFromFile = dataSettings.dataURL
          ? typeof dataSettings.dataURL === 'string'
            ? dataSettings.fileType === 'json'
              ? await fetchAndParseJSON(
                  dataSettings.dataURL,
                  dataSettings.columnsToArray,
                  dataSettings.dataTransformation,
                  debugMode,
                )
              : dataSettings.fileType === 'api'
                ? await fetchAndTransformDataFromAPI(
                    dataSettings.dataURL,
                    dataSettings.apiHeaders,
                    dataSettings.columnsToArray,
                    dataSettings.dataTransformation,
                    debugMode,
                  )
                : await fetchAndParseCSV(
                    dataSettings.dataURL,
                    dataSettings.dataTransformation,
                    dataSettings.columnsToArray,
                    debugMode,
                    dataSettings.delimiter,
                    true,
                  )
            : await fetchAndParseMultipleDataSources(
                dataSettings.dataURL,
                dataSettings.idColumnTitle,
              )
          : await transformColumnsToArray(dataSettings.data, dataSettings.columnsToArray);
        setDataFromFile(fetchDataFromFile);
      } catch (error) {
        console.error('Data fetching error:', error);
      }
    };
    fetchData();
    updateFiltersEvent();
  }, [dataSettings, debugMode]);
  useEffect(() => {
    updateFiltersEvent();
  }, [filters, dataFromFile]);

  const updateGridOptionEvent = useEffectEvent(() => {
    const gridValue = getUniqValue(dataFromFile, columnGridBy) as (string | number)[];
    setGridOption(gridValue);
  });
  useEffect(() => {
    updateGridOptionEvent();
  }, [columnGridBy, dataFromFile]);
  useEffect(() => {
    setGraphConfig(graphDataConfiguration);
  }, [graphDataConfiguration]);
  // biome-ignore lint/suspicious/noExplicitAny: undefined data type
  const handleFilterChange = useCallback((filter: string, values: any) => {
    setFilterSettings((prev) =>
      prev.map((f) => (f.filter === filter ? { ...f, value: values } : f)),
    );
  }, []);
  return (
    <GraphContainer
      className={graphSettings?.classNames?.graphContainer}
      style={graphSettings?.styles?.graphContainer}
      id={graphSettings?.graphID}
      ref={graphParentDiv}
      aria-label={graphSettings?.ariaLabel || 'This is a gridded graph'}
      backgroundColor={graphSettings?.backgroundColor ?? false}
      theme={graphSettings?.theme || 'light'}
      language={graphSettings?.language || 'en'}
      width={graphSettings?.width}
      height={graphSettings?.height}
      padding={graphSettings?.padding}
    >
      {advancedGraphSettings?.graphTitle ||
      advancedGraphSettings?.graphDescription ||
      graphSettings?.graphTitle ||
      graphSettings?.graphDescription ||
      graphSettings?.graphDownload ||
      graphSettings?.dataDownload ? (
        <GraphHeader
          styles={{
            title: advancedGraphSettings?.styles?.title || graphSettings?.styles?.title,
            description:
              advancedGraphSettings?.styles?.description || graphSettings?.styles?.description,
          }}
          classNames={{
            title: advancedGraphSettings?.classNames?.title || graphSettings?.classNames?.title,
            description:
              advancedGraphSettings?.classNames?.description ||
              graphSettings?.classNames?.description,
          }}
          graphTitle={advancedGraphSettings?.graphTitle || graphSettings?.graphTitle}
          graphDescription={
            advancedGraphSettings?.graphDescription || graphSettings?.graphDescription
          }
          width={advancedGraphSettings?.width || graphSettings?.width}
          graphDownload={graphSettings?.graphDownload ? graphParentDiv : undefined}
          dataDownload={
            graphSettings?.dataDownload && data ? (data.length > 0 ? data : null) : null
          }
        />
      ) : null}
      {data && gridOption.length > 0 ? (
        <>
          {filterSettings.length !== 0 ||
          (dataSelectionOptions || []).length !== 0 ||
          (advancedDataSelectionOptions || []).length !== 0 ? (
            <div className='flex flex-wrap items-start gap-x-4 gap-y-0 w-full'>
              {advancedDataSelectionOptions?.map((d, i) => (
                <div
                  style={{
                    width:
                      d.width ||
                      `calc(${100 / noOfFiltersPerRow}% - ${
                        (noOfFiltersPerRow - 1) / noOfFiltersPerRow
                      }rem)`,
                    flexGrow: d.width ? 0 : 1,
                    flexShrink: d.ui !== 'radio' || d.width ? 0 : 1,
                    minWidth: '240px',
                  }}
                  className='pb-4'
                  // biome-ignore lint/suspicious/noArrayIndexKey: index is the unique identifier
                  key={i}
                >
                  <Label className='mb-2'>{d.label || 'Graph by'}</Label>
                  {d.ui !== 'radio' ? (
                    <DropdownSelect
                      options={d.options.map((opt) => ({
                        ...opt,
                        value: opt.label,
                      }))}
                      isClearable={false}
                      isSearchable
                      variant={uiMode}
                      controlShouldRenderValue
                      defaultValue={
                        d.defaultValue
                          ? {
                              ...d.defaultValue,
                              value: d.defaultValue?.label,
                            }
                          : {
                              ...d.options[0],
                              value: d.options[0].label,
                            }
                      }
                      // biome-ignore lint/suspicious/noExplicitAny: undefined data type
                      onChange={(el: any) => {
                        setAdvancedGraphSettings(el?.graphSettings || {});
                        setGraphConfig(el?.dataConfiguration);
                      }}
                    />
                  ) : (
                    <RadioGroup
                      defaultValue={d.defaultValue?.label || d.options[0].label}
                      variant={uiMode}
                      onValueChange={(el) => {
                        const selectedOption =
                          d.options[d.options.findIndex((opt) => opt.label === el)];
                        setAdvancedGraphSettings(selectedOption.graphSettings || {});
                        setGraphConfig(selectedOption.dataConfiguration);
                      }}
                    >
                      {d.options.map((el) => (
                        <RadioGroupItem label={el.label} value={el.label} key={el.label} />
                      ))}
                    </RadioGroup>
                  )}
                </div>
              ))}
              {dataSelectionOptions?.map((d, i) => (
                <div
                  style={{
                    width:
                      d.width ||
                      `calc(${100 / noOfFiltersPerRow}% - ${
                        (noOfFiltersPerRow - 1) / noOfFiltersPerRow
                      }rem)`,
                    flexGrow: d.width ? 0 : 1,
                    flexShrink: d.ui !== 'radio' || d.width ? 0 : 1,
                    minWidth: '240px',
                  }}
                  className='pb-4'
                  // biome-ignore lint/suspicious/noArrayIndexKey: index is the unique identifier
                  key={i}
                >
                  <Label className='mb-2'>{d.label || `Visualize ${d.chartConfigId} by`}</Label>
                  {!checkIfMultiple(d.chartConfigId, graphConfig || []) ? (
                    d.ui !== 'radio' ? (
                      <DropdownSelect
                        options={d.allowedColumnIds}
                        isClearable={false}
                        isSearchable
                        variant={uiMode}
                        defaultValue={
                          graphDataConfiguration
                            ? d.allowedColumnIds[
                                d.allowedColumnIds.findIndex(
                                  (j) =>
                                    j.value ===
                                    (graphDataConfiguration[
                                      graphDataConfiguration.findIndex(
                                        (el) => el.chartConfigId === d.chartConfigId,
                                      )
                                    ].columnId as string),
                                )
                              ]
                            : undefined
                        }
                        controlShouldRenderValue
                        // biome-ignore lint/suspicious/noExplicitAny: undefined data type
                        onChange={(el: any) => {
                          const newGraphConfig = {
                            columnId: el?.value as string,
                            chartConfigId: d.chartConfigId,
                          };
                          const updatedConfig = graphConfig?.map((item) =>
                            item.chartConfigId === newGraphConfig.chartConfigId
                              ? newGraphConfig
                              : item,
                          );
                          setAdvancedGraphSettings(el?.graphSettings || {});
                          setGraphConfig(updatedConfig);
                        }}
                      />
                    ) : (
                      <RadioGroup
                        variant={uiMode}
                        defaultValue={
                          graphDataConfiguration
                            ? d.allowedColumnIds[
                                d.allowedColumnIds.findIndex(
                                  (j) =>
                                    j.value ===
                                    (graphDataConfiguration[
                                      graphDataConfiguration.findIndex(
                                        (el) => el.chartConfigId === d.chartConfigId,
                                      )
                                    ].columnId as string),
                                )
                              ].value
                            : ''
                        }
                        onValueChange={(el) => {
                          const selectedOption =
                            d.allowedColumnIds[
                              d.allowedColumnIds.findIndex((opt) => opt.value === el)
                            ];
                          const newGraphConfig = {
                            columnId: selectedOption.value,
                            chartConfigId: d.chartConfigId,
                          };
                          const updatedConfig = graphConfig?.map((item) =>
                            item.chartConfigId === newGraphConfig.chartConfigId
                              ? newGraphConfig
                              : item,
                          );
                          setAdvancedGraphSettings(selectedOption.graphSettings || {});
                          setGraphConfig(updatedConfig);
                        }}
                      >
                        {d.allowedColumnIds.map((el) => (
                          <RadioGroupItem label={el.label} value={el.value} key={el.value} />
                        ))}
                      </RadioGroup>
                    )
                  ) : d.ui !== 'radio' ? (
                    <DropdownSelect
                      options={d.allowedColumnIds}
                      isMulti
                      variant={uiMode}
                      isSearchable
                      controlShouldRenderValue
                      defaultValue={
                        graphDataConfiguration
                          ? (
                              graphDataConfiguration[
                                graphDataConfiguration.findIndex(
                                  (el) => el.chartConfigId === d.chartConfigId,
                                )
                              ].columnId as string[]
                            ).map(
                              (el) =>
                                d.allowedColumnIds[
                                  d.allowedColumnIds.findIndex((j) => j.value === el)
                                ],
                            )
                          : undefined
                      }
                      filterOption={createFilter(filterConfig)}
                      // biome-ignore lint/suspicious/noExplicitAny: undefined data type
                      onChange={(el: any) => {
                        const newGraphConfig = {
                          columnId: el.map(
                            // biome-ignore lint/suspicious/noExplicitAny: undefined data type
                            (item: any) => item.value,
                          ) as string[],
                          chartConfigId: d.chartConfigId,
                        };
                        const updatedConfig = graphConfig?.map((item) =>
                          item.chartConfigId === newGraphConfig.chartConfigId
                            ? newGraphConfig
                            : item,
                        );
                        setGraphConfig(updatedConfig);
                      }}
                      isRtl={graphSettings?.language === 'ar' || graphSettings?.language === 'he'}
                    />
                  ) : (
                    <CheckboxGroup
                      variant={uiMode}
                      defaultValue={
                        graphDataConfiguration
                          ? (
                              graphDataConfiguration[
                                graphDataConfiguration.findIndex(
                                  (el) => el.chartConfigId === d.chartConfigId,
                                )
                              ].columnId as string[]
                            )
                              .map(
                                (el) =>
                                  d.allowedColumnIds[
                                    d.allowedColumnIds.findIndex((j) => j.value === el)
                                  ],
                              )
                              .map((el) => el.value)
                          : []
                      }
                      onValueChange={(el) => {
                        const newGraphConfig = {
                          columnId: el || [],
                          chartConfigId: d.chartConfigId,
                        };
                        const updatedConfig = graphConfig?.map((item) =>
                          item.chartConfigId === newGraphConfig.chartConfigId
                            ? newGraphConfig
                            : item,
                        );
                        setGraphConfig(updatedConfig);
                      }}
                    >
                      {d.allowedColumnIds.map((el) => (
                        <CheckboxGroupItem label={el.label} value={el.value} key={el.value} />
                      ))}
                    </CheckboxGroup>
                  )}
                </div>
              ))}
              {filterSettings?.map((d, i) => (
                <div
                  style={{
                    width:
                      d.width ||
                      `calc(${100 / noOfFiltersPerRow}% - ${
                        (noOfFiltersPerRow - 1) / noOfFiltersPerRow
                      }rem)`,
                    flexGrow: d.width ? 0 : 1,
                    flexShrink: d.ui !== 'radio' || d.width ? 0 : 1,
                    flexWrap: 'wrap',
                  }}
                  className='pb-4'
                  // biome-ignore lint/suspicious/noArrayIndexKey: index is the unique identifier
                  key={i}
                >
                  <Label className='mb-2'>{d.label}</Label>
                  {d.singleSelect ? (
                    d.ui !== 'radio' ? (
                      <DropdownSelect
                        options={d.availableValues}
                        variant={uiMode}
                        isClearable={d.clearable === undefined ? true : d.clearable}
                        isSearchable
                        controlShouldRenderValue
                        filterOption={createFilter(filterConfig)}
                        onChange={(el) => {
                          handleFilterChange(d.filter, el);
                        }}
                        value={d.value}
                        defaultValue={d.defaultValue}
                      />
                    ) : (
                      <RadioGroup
                        variant={uiMode}
                        defaultValue={(d.defaultValue as { value: string; label: string }).value}
                        onValueChange={(el) => {
                          handleFilterChange(
                            d.filter,
                            d.availableValues.filter((v) => v.value === el),
                          );
                        }}
                      >
                        {d.availableValues.map((el) => (
                          <RadioGroupItem
                            label={`${el.label}`}
                            value={`${el.value}`}
                            key={el.value}
                          />
                        ))}
                      </RadioGroup>
                    )
                  ) : (
                    <>
                      {d.ui !== 'radio' ? (
                        <DropdownSelect
                          options={d.availableValues}
                          variant={uiMode}
                          size='sm'
                          isMulti
                          isClearable={d.clearable === undefined ? true : d.clearable}
                          isSearchable
                          controlShouldRenderValue
                          filterOption={createFilter(filterConfig)}
                          onChange={(el) => {
                            handleFilterChange(d.filter, el);
                          }}
                          value={d.value}
                          defaultValue={d.defaultValue}
                        />
                      ) : (
                        <CheckboxGroup
                          variant={uiMode}
                          defaultValue={
                            d.defaultValue
                              ? (
                                  d.defaultValue as {
                                    value: string | number;
                                    label: string | number;
                                  }[]
                                ).map((el) => `${el.value}`)
                              : []
                          }
                          value={
                            d.value
                              ? (
                                  d.value as {
                                    value: string | number;
                                    label: string | number;
                                  }[]
                                ).map((el) => `${el.value}`)
                              : undefined
                          }
                          onValueChange={(el) => {
                            handleFilterChange(
                              d.filter,
                              d.availableValues.filter((v) => el.indexOf(`${v.value}`) !== -1),
                            );
                          }}
                        >
                          {d.availableValues.map((el) => (
                            <CheckboxGroupItem
                              label={`${el.label}`}
                              value={`${el.value}`}
                              key={el.value}
                            />
                          ))}
                        </CheckboxGroup>
                      )}
                      {d.allowSelectAll ? (
                        <button
                          type='button'
                          className='bg-transparent border-0 p-0 mt-2 cursor-pointer text-info'
                          onClick={() => {
                            handleFilterChange(d.filter, d.availableValues);
                          }}
                        >
                          Select all options
                        </button>
                      ) : null}
                    </>
                  )}
                </div>
              ))}
            </div>
          ) : null}
          {showCommonColorScale !== false && graphSettings?.showColorScale !== false ? (
            <ColorLegend
              width={graphSettings?.width}
              colorLegendTitle={graphSettings?.colorLegendTitle}
              colors={
                (graphSettings?.colors as string[] | undefined) ||
                (graphSettings?.lineColors as string[] | undefined) ||
                Colors[(graphSettings?.theme as 'light' | 'dark' | undefined) || 'light']
                  .categoricalColors.colors
              }
              colorDomain={graphSettings?.colorDomain || graphSettings?.labels || []}
              showNAColor={
                graphSettings?.showNAColor === undefined || graphSettings?.showNAColor === null
                  ? true
                  : graphSettings?.showNAColor
              }
            />
          ) : null}
          <div
            className={`flex flex-wrap gap-4 justify-center ${graphSettings?.language === 'ar' || graphSettings?.language === 'he' ? 'flex-row-reverse' : 'flex-row'}`}
          >
            {gridOption.map((el) => (
              <div
                key={el}
                style={{
                  width: `calc(${
                    100 / (noOfColumns || (Math.min(...[4, gridOption.length || 0]) as number))
                  }% - ${
                    ((noOfColumns || (Math.min(...[4, gridOption.length || 0]) as number)) - 1) /
                    (noOfColumns || (Math.min(...[4, gridOption.length || 0]) as number))
                  }rem)`,
                  minWidth: checkIfNullOrUndefined(minGraphWidth) ? '280px' : `${minGraphWidth}px`,
                }}
              >
                <GraphEl
                  graph={graphType}
                  graphDataConfiguration={graphConfig}
                  graphData={
                    transformDataForGraph(
                      dataTransform
                        ? transformDataForAggregation(
                            filterData(data, dataFilters || []).filter(
                              // biome-ignore lint/suspicious/noExplicitAny: undefined data type
                              (d: any) => d[columnGridBy] === el,
                            ),
                            dataTransform.keyColumn,
                            dataTransform.aggregationColumnsSetting,
                          )
                        : filterData(data, dataFilters || []).filter(
                            // biome-ignore lint/suspicious/noExplicitAny: undefined data type
                            (d: any) => d[columnGridBy] === el,
                          ),
                      graphType,
                      graphConfig,
                    ) || []
                  }
                  debugMode={debugMode}
                  settings={{
                    ...(graphSettings || {}),
                    ...advancedGraphSettings,
                    theme: graphSettings?.theme || theme,
                    width: undefined,
                    height: undefined,
                    relativeHeight: graphSettings?.relativeHeight || 0.67,
                    minHeight: minGraphHeight,
                    graphTitle: `${el}`,
                    graphDescription: undefined,
                    graphDownload: false,
                    dataDownload: false,
                    backgroundColor: undefined,
                    padding: '0',
                    footNote: undefined,
                    sources: undefined,
                    showColorScale:
                      graphSettings?.showColorScale === false
                        ? false
                        : showCommonColorScale === false,
                  }}
                  readableHeader={readableHeader || []}
                />
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className='w-full flex justify-center p-4'>
          <Spinner />
        </div>
      )}
      {graphSettings?.sources || graphSettings?.footNote ? (
        <GraphFooter
          styles={{
            footnote: graphSettings?.styles?.footnote,
            source: graphSettings?.styles?.source,
          }}
          classNames={{
            footnote: graphSettings?.classNames?.footnote,
            source: graphSettings?.classNames?.source,
          }}
          sources={graphSettings?.sources}
          footNote={graphSettings?.footNote}
          width={graphSettings?.width}
        />
      ) : null}
    </GraphContainer>
  );
}
